<template>
  <span :class="[linkClass, sizeClass]" :style="{ fontWeight: props.fontWeight }">
    <a
      :href="isExternalLink ? props.href : undefined"
      :target="newTab ? '_blank' : '_self'"
      :rel="newTab ? 'noopener noreferrer' : undefined"
      :role="isExternalLink && hasHref ? undefined : 'button'"
      :class="['link-text', { disabled }]"
      @click="handleClick"
      @keydown.enter="handleClick"
      @keydown.space="handleClick"
      @mouseover="handleMouseOver"
      @mouseleave="handleMouseLeave"
      :aria-label="ariaLabel || label"
      tabindex="0"
    >
      <v-icon
        v-if="(variant === 'standalone' || variant === 'icon-only') && prependIcon"
        :icon="prependIcon"
        :size="iconSizeComputed"
        :style="{ color: currentColor, marginRight: '6px' }"
        class="link-icon"
        aria-hidden="true"
      />

      <span v-if="variant !== 'icon-only'">{{ label }}</span>

      <v-icon
        v-if="(variant === 'standalone' || variant === 'icon-only') && appendIcon"
        :icon="appendIcon"
        :size="iconSizeComputed"
        :style="{ color: currentColor, marginLeft: '6px' }"
        class="link-icon"
        aria-hidden="true"
      />
    </a>
  </span>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

const emits = defineEmits(['click'])
const router = useRouter()

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String },
  href: { default: null },
  newTab: { type: Boolean, default: false },
  ariaLabel: { type: String, default: null },
  variant: {
    type: String,
    default: 'standalone',
    validator: (value) => ['standalone', 'inline', 'icon-only'].includes(value),
  },
  size: {
    type: String,
    default: 'large',
    validator: (value) => ['x-large', 'large', 'medium', 'small'].includes(value),
  },
  iconSize: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  styleClass: { type: String, default: null },
  fontWeight: { type: Number, default: 600 },
  appendIcon: { type: String, default: null },
  prependIcon: { type: String, default: null },
  defaultIconColor: { type: String, default: 'var(--kds-on-surface)' },
  hoverIconColor: { type: String, default: 'var(--kds-on-surface)' },
  goBack: { type: Boolean, default: false },
})

const isExternalLink = computed(() => {
  return (
    typeof props.href === 'string' &&
    (props.href?.startsWith('http') ||
      props.href?.startsWith('www.') ||
      props.href?.startsWith('tel:') ||
      props.href?.startsWith('mailto:'))
  )
})

const hasHref = computed(() => !!props.href)

const linkClass = computed(() => ({
  [props.styleClass]: !!props.styleClass,
  [`link--${props.variant}`]: true,
  disabled: props.disabled,
  ['with-icon']: (props.variant === 'standalone' || props.variant === 'icon-only') && !!props.appendIcon,
}))

const sizeClass = computed(() => (props.iconSize === null ? `link--${props.size}` : ''))

const iconSizeComputed = computed(() => {
  if (props.variant === 'icon-only' && props.iconSize) {
    return props.iconSize
  } else {
    switch (props.size) {
      case 'x-large':
        return '24px'
      case 'large':
        return '20px'
      default:
        return '16px'
    }
  }
})

const currentColor = ref(props.variant === 'icon-only' ? props.defaultIconColor : 'inherit')

const handleClick = (event) => {
  if (props.disabled) return

  if (props.goBack) {
    event.preventDefault()
    const savedURL = sessionStorage.getItem('prevDlDashboardURL')
    if (savedURL) {
      router.push(savedURL)
    } else {
      router.back()
    }
    return
  }

  if (!isExternalLink.value && props.href) {
    event.preventDefault()
    router.push(props.href)
    return
  }

  if (props.variant === 'icon-only') {
    currentColor.value = props.hoverIconColor
    setTimeout(() => {
      currentColor.value = props.defaultIconColor
    }, 200)
  }

  emits('click', event)
}

const handleMouseOver = () => {
  if (!props.disabled && currentColor.value === props.defaultIconColor) {
    currentColor.value = props.hoverIconColor
  }
}

const handleMouseLeave = () => {
  if (!props.disabled && currentColor.value === props.hoverIconColor) {
    currentColor.value = props.defaultIconColor
  }
}
</script>

<style lang="scss" scoped>
@use '@/styles/colors' as c;

span.link--standalone a:focus {
  outline: 1px solid var(--kds-focus);
  outline-offset: 2px;
}

span {
  cursor: pointer;
  text-transform: none;
  padding-left: 0px;
  padding-right: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  max-width: 100%;

  &.link--standalone {
    a {
      color: var(--kds-primary);
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      white-space: normal;
      word-wrap: break-word;

      span {
        flex: 0 1 auto;
      }

      .link-icon {
        flex-shrink: 0;
      }

      &:hover {
        text-decoration: underline;
        color: var(--kds-on-surface);
      }

      &:active {
        span {
          color: var(--kds-on-surface-variant);
          text-decoration: underline;
          text-decoration-color: var(--kds-on-surface-variant);
        }
      }

      &:focus,
      &:focus-visible {
        height: 100%;
      }
    }
  }

  &.link--icon-only {
    a {
      &:focus,
      &:focus-visible {
        outline: unset;
        .link-icon {
          outline: 1px solid var(--kds-focus);
        }
      }
    }

    &.disabled {
      color: c.$p-grey-disabled;

      a {
        pointer-events: none;
        color: inherit;
      }
    }
  }

  &.link--inline {
    color: var(--kds-primary);

    a {
      color: inherit;
      span {
        text-decoration: underline;
      }
      &:hover {
        color: var(--kds-primary);
        text-decoration: underline;
      }
      &:active {
        span {
          color: var(--kds-on-surface-variant);
          text-decoration: underline;
          text-decoration-color: var(--kds-on-surface-variant);
        }
      }
      &:focus {
        outline: 1px solid var(--kds-focus);
      }
    }

    &.disabled {
      color: c.$p-grey-disabled;

      a {
        pointer-events: none;
        color: inherit;
      }
    }
  }

  &.link--small {
    height: 16px;
    font-size: 11px;
  }

  &.link--medium {
    height: 16px;
    font-size: 0.75rem;
  }

  &.link--large {
    height: 20px;
    font-size: 0.875rem;
  }
}
</style>
